import {isMobile} from '../../util/panelbankFlow';
import {DEFAULT_FLOW_CONFIG_BOX_WIDTH} from '../../util/sectionFlowConfig';
import {
  getFlowConfigMaxRowsPerPage,
  getFlowConfigPageSizeOptions,
} from './panelBankFlowPaginationUtils';
import {PanelBankFlowSectionConfig} from './types';
import {DEFAULT_SECTION_GAP} from './utils';

export const getPanelDimensions = ({
  panelBankWidth,
  flowConfig,
}: {
  panelBankWidth: number;
  flowConfig: PanelBankFlowSectionConfig;
}) => {
  const mobile = isMobile();

  if (mobile) {
    return {
      panelWidth: panelBankWidth,
      panelHeight: DEFAULT_FLOW_CONFIG_BOX_WIDTH,
    };
  }

  if (flowConfig.snapToColumns) {
    const columnCount = flowConfig.columnsPerPage;
    const columnWidth = getWidthOfColumn({
      columnCount,
      maxWidth: panelBankWidth,
    });

    return {
      panelWidth: columnWidth,
      panelHeight: flowConfig.boxHeight,
    };
  }

  return {
    panelWidth: flowConfig.boxWidth,
    panelHeight: flowConfig.boxHeight,
  };
};

export const getRowsPerPageWithHeight = ({
  maxRows,
  panelHeight,
  sectionHeight,
}: {
  /**
   * Number of possible rows a section can have by taking into account
   * the number of total panels that are in a section.
   */
  maxRows: number;
  panelHeight: number;
  sectionHeight: number;
}) => {
  const rowsPerPage = Math.max(
    1,
    Math.min(
      maxRows,
      Math.floor((sectionHeight - DEFAULT_SECTION_GAP) / panelHeight)
    )
  );

  return rowsPerPage;
};

/**
 * This returns the new rows per page after user finishes resizing a panel.
 * It needs to consider the section pagination options.
 */
export const getSnappedRowsPerPage = ({
  numVisiblePanels,
  columnsPerPage,
  numTotalPanelsInSection,
}: {
  numVisiblePanels: number;
  columnsPerPage: number;
  numTotalPanelsInSection: number;
}) => {
  // We want to find the closest page size option to the current number of panels
  // so the page size option is selected correctly.
  const pageSizeOptions = getFlowConfigPageSizeOptions({
    columnsPerPage,
    numTotalPanelsInSection,
  });

  // Find the closest page size option to the current number of panels
  let closestOption: number | 'all' = columnsPerPage;
  let minDifference = Infinity;

  for (const option of pageSizeOptions) {
    const optionValue = option === 'all' ? numTotalPanelsInSection : option;
    const difference = Math.abs(optionValue - numVisiblePanels);

    if (difference < minDifference) {
      minDifference = difference;
      closestOption = option;
    }
  }

  // Calculate rows per page based on the closest page size option
  const rowsPerPage = getFlowConfigMaxRowsPerPage({
    columnsPerPage,
    numTotalPanelsInSection,
    option: closestOption,
  });

  return rowsPerPage;
};

export const getSectionHeight = ({
  numVisiblePanels,
  panelsPerRow,
  panelHeight,
}: {
  numVisiblePanels: number;
  panelsPerRow: number;
  panelHeight: number;
}) => {
  if (numVisiblePanels === 0) {
    return 0;
  }

  const actualRows = Math.ceil(numVisiblePanels / panelsPerRow);
  return actualRows * panelHeight + (actualRows - 1) * DEFAULT_SECTION_GAP;
};

export const getMaxSectionWidth = ({
  columnsPerPage,
  panelWidth,
}: {
  columnsPerPage: number;
  panelWidth: number;
}) => {
  if (columnsPerPage === 0) {
    console.error(
      'getMaxSectionWidth called with 0 columns. This should never happen.'
    );
    return 0;
  }

  return (
    columnsPerPage * panelWidth + (columnsPerPage - 1) * DEFAULT_SECTION_GAP
  );
};

export const getWidthOfColumn = ({
  columnCount,
  maxWidth,
}: {
  columnCount: number;
  maxWidth: number;
}) => {
  if (columnCount === 0) {
    console.error(
      'getWidthOfColumn called with 0 columns. This should never happen.'
    );
    return 0;
  }

  return Math.floor(
    (maxWidth - (columnCount - 1) * DEFAULT_SECTION_GAP) / columnCount
  );
};

// Given a px width, returns the closest number of columns to accomodate that width.
// (Enables 'snap to columns' behavior when resizing panels.)
const WORKSPACE_SNAP_COLUMN_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8];
export const getSnappedColumn = ({
  currPanelWidth,
  maxWidth,
}: {
  currPanelWidth: number;
  maxWidth: number;
}) => {
  let closestIndex = 0;

  for (let i = 0; i < WORKSPACE_SNAP_COLUMN_COUNTS.length; i++) {
    const snapColumnWidth = getWidthOfColumn({
      columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[i],
      maxWidth,
    });

    const closestWidth = getWidthOfColumn({
      columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[closestIndex],
      maxWidth,
    });

    if (
      Math.abs(currPanelWidth - snapColumnWidth) <
      Math.abs(currPanelWidth - closestWidth)
    ) {
      closestIndex = i;
    }
  }

  return {
    columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[closestIndex],
    width: getWidthOfColumn({
      columnCount: WORKSPACE_SNAP_COLUMN_COUNTS[closestIndex],
      maxWidth,
    }),
  };
};

/**
 * Calculates the CSS transform for positioning a panel in a grid layout
 * using hardware-accelerated translate3d transforms.
 *
 * @param index - The panel's position in the array (0-based)
 * @returns CSS transform string for positioning the panel
 */
export const getPanelTransform = ({
  index,
  columnsPerPage,
  panelWidth,
  panelHeight,
}: {
  index: number;
  columnsPerPage: number;
  panelWidth: number;
  panelHeight: number;
}): string => {
  if (columnsPerPage === 0) {
    console.error(
      'getPanelTransform called with 0 columns. This should never happen.'
    );
    return 'translate3d(0px, 0px, 0)';
  }

  const row = Math.floor(index / columnsPerPage);
  const col = index % columnsPerPage;
  const x = col * (panelWidth + DEFAULT_SECTION_GAP);
  const y = row * (panelHeight + DEFAULT_SECTION_GAP);

  // Intentionally using translate3d to take advantage of GPU acceleration
  return `translate3d(${x}px, ${y}px, 0)`;
};
